import { cn } from "@/lib/utils"

// Decorative marks grown from the Sanctuary logo — a sky-blue disc inside
// hand-drawn, slightly broken rings. Each variant is the logo taken apart:
// the rings, the filled disc, the ink speckle of the brush, plus one sprig
// for the rooms. Always decorative (aria-hidden, no pointer events), always
// behind the content: the parent sets `relative overflow-hidden` and gives
// its content `relative` so it paints on top.
//
// Rules from the design canvas: one motif per section, two at most on a
// hero, never over a face, never on a form, palette colours only.

export type BrandMotifVariant = "rings" | "disc" | "speckle" | "sprig"
export type BrandMotifTone = "navy-light" | "white" | "sage" | "sky"

const TONES: Record<BrandMotifTone, string> = {
  "navy-light": "#ADC2EA",
  white: "#FFFFFF",
  sage: "#B4CABA",
  sky: "#98B5D3",
}

interface Props {
  variant: BrandMotifVariant
  /** Colour; defaults per variant (rings navy-light, disc sky, speckle sage, sprig sage). */
  tone?: BrandMotifTone
  /** Rendered size in px — the mark is square. */
  size?: number
  /** Overall opacity, 0–1. Defaults per variant. */
  opacity?: number
  /** Positioning classes — `absolute -top-40 -right-32` etc. */
  className?: string
  /** Speckle only: a different seed gives a different scatter. */
  seed?: number
}

const DEFAULTS: Record<BrandMotifVariant, { tone: BrandMotifTone; opacity: number; size: number }> = {
  rings: { tone: "navy-light", opacity: 0.55, size: 560 },
  disc: { tone: "sky", opacity: 0.16, size: 420 },
  speckle: { tone: "sage", opacity: 0.9, size: 320 },
  sprig: { tone: "sage", opacity: 0.9, size: 320 },
}

// A tiny deterministic generator so the speckle is identical on the server
// and in the browser — Math.random() would break hydration.
function scatter(seed: number, count: number) {
  let s = seed * 9301 + 49297
  const next = () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
  const radii = [2, 2.5, 3, 4, 5, 7, 9, 12]
  return Array.from({ length: count }, () => ({
    x: 20 + next() * 360,
    y: 20 + next() * 360,
    r: radii[Math.floor(next() * radii.length)],
    o: 0.35 + next() * 0.65,
  }))
}

export function BrandMotif({ variant, tone, size, opacity, className, seed = 3 }: Props) {
  const d = DEFAULTS[variant]
  const colour = TONES[tone ?? d.tone]
  const px = size ?? d.size
  const alpha = opacity ?? d.opacity
  const shared = {
    "aria-hidden": true as const,
    width: px,
    height: px,
    viewBox: "0 0 400 400",
    className: cn("pointer-events-none select-none", className),
    style: { opacity: alpha },
  }

  switch (variant) {
    case "rings":
      return (
        <svg {...shared} fill="none">
          <circle cx="200" cy="200" r="150" stroke={colour} strokeWidth="2.2" />
          <circle cx="191" cy="207" r="168" stroke={colour} strokeWidth="1.6" opacity="0.8" strokeDasharray="880 70" />
          <circle cx="207" cy="193" r="188" stroke={colour} strokeWidth="1.2" opacity="0.6" strokeDasharray="1040 120" strokeDashoffset="300" />
        </svg>
      )
    case "disc":
      return (
        <svg {...shared}>
          <circle cx="200" cy="200" r="190" fill={colour} />
        </svg>
      )
    case "speckle":
      return (
        <svg {...shared}>
          {scatter(seed, 22).map((p, i) => (
            <circle key={i} cx={p.x.toFixed(0)} cy={p.y.toFixed(0)} r={p.r} fill={colour} opacity={p.o.toFixed(2)} />
          ))}
        </svg>
      )
    case "sprig":
      return (
        <svg {...shared} fill="none">
          <path d="M200 370 C 196 300, 204 200, 200 80" stroke={colour} strokeWidth="1.8" strokeLinecap="round" />
          {Array.from({ length: 7 }, (_, i) => {
            const side = i % 2 === 0 ? 1 : -1
            const y = 330 - i * 36
            return (
              <path
                key={i}
                d={`M${200 + side * 10} ${y} c ${side * 38} -10 ${side * 46} -34 ${side * 40} -46 c -18 10 -34 30 -40 46 z`}
                stroke={colour}
                strokeWidth="1.6"
                fill={colour}
                fillOpacity="0.18"
              />
            )
          })}
        </svg>
      )
  }
}

export default BrandMotif
